import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/bporh_admissoes.css';

// Página de Férias do BPO RH — espelho da Convenia (solicitações + períodos aquisitivos).
// Fluxo: empresas com férias → colaboradores → detalhe (solicitações e saldos).
// Mesmo layout das Admissões/Rescisões: cards adm-*, painel-resumo e filtros.

// Etapas do fluxo de aprovação da Convenia — cada uma com sua cor, no mesmo
// padrão de COR_ETAPA_CONVENIA das Admissões. A ordem segue o fluxo real:
// gestor → RH → contabilidade → assinatura → aprovada.
const ETAPA_CONVENIA = {
  supervisorApproval: { rotulo: 'Aprovação Gestor', passo: '1/4', bg: '#fff5da', cor: '#8a6d00', borda: '#f1be48' },
  hrApproval: { rotulo: 'Aprovação RH', passo: '2/4', bg: '#ffeede', cor: '#9a4a06', borda: '#f97316' },
  accounting: { rotulo: 'Contabilidade', passo: '3/4', bg: '#f1e8ff', cor: '#5b21b6', borda: '#8b5cf6' },
  signature: { rotulo: 'Assinatura', passo: '4/4', bg: '#e3f6fb', cor: '#0e6e86', borda: '#00b5cc' },
  approved: { rotulo: 'Aprovada', bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971' },
  canceled: { rotulo: 'Cancelada', bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545' },
};

const BadgeEtapa = ({ status }) => {
  const e = ETAPA_CONVENIA[status];
  return (
    <span className="badge fw-semibold"
      style={{
        backgroundColor: e?.bg || '#f2f4f8',
        color: e?.cor || '#475467',
        border: `1.5px solid ${e?.borda || '#cbd5e1'}`,
      }}
      title={e?.passo ? `Passo ${e.passo} do fluxo de aprovação da Convenia` : undefined}>
      {e?.rotulo || status || '—'}
      {e?.passo && (
        <span className="ms-1" style={{ opacity: .75, fontVariantNumeric: 'tabular-nums' }}>
          {e.passo}
        </span>
      )}
    </span>
  );
};

// Vínculo empregatício como está cadastrado na Convenia (vem do histórico
// salarial vigente — $.relationship.name). `modelo_origem` diz de onde veio:
// 'convenia' = dado real; 'cargo' = presunção pelo nome do cargo, para os poucos
// colaboradores sem histórico salarial no espelho.
const MODELO_CONTRATACAO = {
  'Pessoa Jurídica': { bg: '#f1e8ff', cor: '#5b21b6', borda: '#8b5cf6',
    dica: 'Pessoa Jurídica — não tem férias CLT; cobrança de prazo não se aplica' },
  PJ: { bg: '#f1e8ff', cor: '#5b21b6', borda: '#8b5cf6',
    dica: 'Pessoa Jurídica — não tem férias CLT; cobrança de prazo não se aplica' },
  'Sócio': { bg: '#ffeede', cor: '#9a4a06', borda: '#f97316',
    dica: 'Sócio — não tem férias CLT; cobrança de prazo não se aplica' },
  'Estágio': { bg: '#e3f6fb', cor: '#0e6e86', borda: '#00b5cc',
    dica: 'Estágio — recesso remunerado pela Lei 11.788, regra diferente da CLT' },
  Aprendiz: { bg: '#fff5da', cor: '#8a6d00', borda: '#f1be48',
    dica: 'Jovem Aprendiz — férias coincidem com as férias escolares (art. 136, §2º)' },
  'Trabalhador Autônomo': { bg: '#f1e8ff', cor: '#5b21b6', borda: '#8b5cf6',
    dica: 'Trabalhador autônomo — sem férias CLT' },
  'Contrato por Tempo Determinado': { bg: '#eef3ff', cor: '#1d4ed8', borda: '#3b82f6',
    dica: 'Contrato por tempo determinado — férias proporcionais ao período contratado' },
  CLT: { bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971',
    dica: 'CLT — férias na regra geral da CLT' },
};

// Rótulo curto para caber na coluna, mantendo o nome da Convenia no tooltip
const MODELO_CURTO = { 'Pessoa Jurídica': 'PJ', 'Contrato por Tempo Determinado': 'Tempo determ.',
  'Trabalhador Autônomo': 'Autônomo' };

const BadgeModelo = ({ modelo, origem }) => {
  if (!modelo) return <span className="text-muted">-</span>;
  const m = MODELO_CONTRATACAO[modelo]
    || { bg: '#f2f4f8', cor: '#475467', borda: '#cbd5e1', dica: `Vínculo na Convenia: ${modelo}` };
  const presumido = origem === 'cargo';
  return (
    <span className="badge fw-semibold"
      style={{
        backgroundColor: presumido ? '#f2f4f8' : m.bg,
        color: presumido ? '#475467' : m.cor,
        border: `1.5px solid ${presumido ? '#cbd5e1' : m.borda}`,
      }}
      title={presumido
        ? `${modelo} PRESUMIDO pelo cargo — este colaborador não tem histórico salarial `
          + `no espelho da Convenia, então o vínculo não pôde ser lido`
        : `${m.dica} · vínculo cadastrado na Convenia`}>
      {MODELO_CURTO[modelo] || modelo}
      {presumido && <span className="ms-1" style={{ opacity: .6 }}>?</span>}
    </span>
  );
};

// Prazo de PAGAMENTO das férias (CLT art. 145): a Comece conta 2 dias ÚTEIS antes do
// início — sábado, domingo e feriado não contam (início na segunda paga na quinta).
// O backend já devolve a data-limite calculada.
const BadgePagamento = ({ s }) => {
  if (!s?.pagamento_ate) return <span className="text-muted">-</span>;
  const dias = s.pagamento_dias;
  const nota = s.pagamento_antecipado
    ? ' Fim de semana ou feriado no caminho: contando só dias úteis, o limite ficou antes.'
    : '';
  // documento das férias já foi ao financeiro pelo módulo Financeiro: está resolvido
  if (s.pagamento_enviado) {
    return (
      <span className="badge fw-semibold" style={{
        backgroundColor: '#e6f7ef', color: '#116b47', border: '1.5px solid #1fa971',
        whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
        title={`Enviado ao financeiro em ${s.pagamento_enviado.em}${s.pagamento_enviado.por ? ` por ${s.pagamento_enviado.por}` : ''}. `
          + `Limite de pagamento: ${s.pagamento_ate}.${nota}`}>
        <i className="fas fa-circle-check me-1"></i>
        enviado ao financeiro {s.pagamento_enviado.em.slice(0, 5)}
      </span>
    );
  }
  // em andamento / encerrada / cancelada: a data fica como referência, sem cobrança de prazo
  if (dias == null) {
    return (
      <span className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}
        title={`Limite de pagamento era ${s.pagamento_ate}.${nota}`}>
        {s.pagamento_ate}
      </span>
    );
  }
  // Sem férias marcadas não há data agendada: o que existe é a PIOR HIPÓTESE —
  // começando no último dia que a Convenia aceita ('Solicitar até'), o pagamento
  // sai 2 dias úteis antes dele.
  // Fica em tom neutro de propósito, para não ser lido como data firmada.
  if (s.pagamento_no_limite) {
    return (
      <span className="badge fw-semibold" style={{
        backgroundColor: '#f2f4f8', color: '#475467', border: '1.5px dashed #cbd5e1',
        whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
        title={'Data de pagamento no LIMITE — este colaborador ainda não marcou férias. '
          + `Se começarem no último dia que a Convenia aceita (${s.pagamento_base}), o pagamento tem `
          + `de sair até ${s.pagamento_ate}. Marcando antes, o pagamento também antecipa.${nota}`}>
        <i className="fas fa-hourglass-end me-1"></i>
        até {s.pagamento_ate}
        <span className="ms-1" style={{ opacity: .75, fontWeight: 500 }}>· no limite</span>
      </span>
    );
  }
  const est = dias < 0
    ? { bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545', icone: 'fa-circle-exclamation',
        txt: dias === -1 ? 'venceu ontem' : `venceu há ${Math.abs(dias)} dias` }
    : dias === 0
    ? { bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545', icone: 'fa-circle-exclamation', txt: 'é hoje' }
    : dias <= 2
    ? { bg: '#ffeede', cor: '#9a4a06', borda: '#f97316', icone: 'fa-triangle-exclamation',
        txt: dias === 1 ? 'amanhã' : `em ${dias} dias` }
    : dias <= 7
    ? { bg: '#fff5da', cor: '#8a6d00', borda: '#f1be48', icone: 'fa-clock', txt: `em ${dias} dias` }
    : { bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971', icone: 'fa-circle-check', txt: `em ${dias} dias` };
  return (
    <span className="badge fw-semibold" style={{
      backgroundColor: est.bg, color: est.cor, border: `1.5px solid ${est.borda}`,
      whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
      title={`Pagar as férias até ${s.pagamento_ate} (2 dias úteis antes do início).${nota}`}>
      <i className={`fas ${est.icone} me-1`}></i>
      {s.pagamento_ate}
      <span className="ms-1" style={{ opacity: .8, fontWeight: 500 }}>· {est.txt}</span>
    </span>
  );
};

// De quem a solicitação depende. A Contabilidade NÃO é fila nossa — sai do
// alerta de férias e aqui aparece como responsável próprio, para a tabela de
// pendências não dizer que a bola está com o operador quando não está.
// Anotação do operador nas férias. Vive em tabela nossa (bporh_ferias_obs), não
// na Convenia — o sync sobrescreve o espelho e apagaria o que foi escrito aqui.
// escopo 'solicitacao' anota aquele período; 'colaborador' é a nota geral,
// usada por quem está a vencer e ainda não marcou nada.
const AnotacaoFerias = ({ clienteId, escopo, chave, obs, onSalvo, rotulo }) => {
  const [editando, setEditando] = React.useState(false);
  const [texto, setTexto] = React.useState(obs?.texto || '');
  const [salvando, setSalvando] = React.useState(false);

  React.useEffect(() => { setTexto(obs?.texto || ''); }, [obs, chave]);

  const salvar = () => {
    setSalvando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bporh/ferias/obs/`,
        { cliente_id: clienteId, escopo, chave, obs: texto },
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setEditando(false);
        if (onSalvo) onSalvo(res.data.obs || null);
      })
      .finally(() => setSalvando(false));
  };

  if (editando) {
    return (
      <div className="mt-2">
        <textarea
          className="form-control form-control-sm"
          rows={2}
          maxLength={1000}
          autoFocus
          placeholder={rotulo || 'Anotação sobre estas férias...'}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); salvar(); }
            if (e.key === 'Escape') { setTexto(obs?.texto || ''); setEditando(false); }
          }}
        />
        <div className="d-flex gap-1 mt-1">
          <button className="btn btn-sm btn-success p-1 px-2" disabled={salvando}
            onClick={salvar} title="Salvar (Enter)">
            <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
          </button>
          <button className="btn btn-sm btn-light border p-1 px-2" disabled={salvando}
            onClick={() => { setTexto(obs?.texto || ''); setEditando(false); }}
            title="Cancelar (Esc)">
            <i className="fas fa-times text-muted"></i>
          </button>
          <span className="small text-muted align-self-center ms-1">
            Enter salva · Shift+Enter quebra linha · vazio apaga
          </span>
        </div>
      </div>
    );
  }

  if (!obs?.texto) {
    return (
      <button type="button" className="btn btn-link btn-sm p-0 mt-2"
        style={{ fontSize: '.78rem', textDecoration: 'none' }}
        onClick={() => setEditando(true)}>
        <i className="fas fa-comment-medical me-1"></i>
        {rotulo || 'Anotar'}
      </button>
    );
  }

  return (
    <div className="small mt-2 p-2 rounded"
      style={{ background: '#fff8e1', border: '1px solid #f0dfa8', cursor: 'pointer' }}
      onClick={() => setEditando(true)}
      title="Clique para editar a anotação">
      <i className="fas fa-comment-dots me-1" style={{ color: '#8a6d00' }}></i>
      <span style={{ whiteSpace: 'pre-wrap' }}>{obs.texto}</span>
      <span className="text-muted ms-2" style={{ fontSize: '.72rem' }}>
        {obs.autor ? `${obs.autor} · ` : ''}{obs.em}
      </span>
    </div>
  );
};

// Afastamento / licença (29/09/2026): quem está afastado (INSS, licença-
// maternidade…) sai dos alertas, do modal de atrasos e do boletim. A cada 30
// dias o lembrete vence e a tela pergunta se continua afastado ou se voltou.
// rótulos genéricos (LGPD, dado de saúde): não dizem a causa do afastamento
const MOTIVOS_AFASTAMENTO = {
  afastamento_inss: 'Afastamento pelo INSS',
  licenca_maternidade: 'Licença-maternidade',
  licenca_nao_remunerada: 'Licença não remunerada',
  outro: 'Outro afastamento / licença',
};

const AfastamentoFerias = ({ item, onMudou }) => {
  const af = item?.afastamento;
  const [abrindo, setAbrindo] = React.useState(false);
  const [motivo, setMotivo] = React.useState('');
  const [obs, setObs] = React.useState('');
  const [salvando, setSalvando] = React.useState('');

  const enviar = (acao) => {
    setSalvando(acao);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bporh/ferias/afastamento/`,
        { cliente_id: item.cliente_id, convenia_id: item.convenia_id, acao, motivo, obs },
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        notificar(res.data.mensagem || 'Salvo');
        setAbrindo(false); setMotivo(''); setObs('');
        if (onMudou) onMudou(acao, res.data.afastamento || null);
      })
      .catch((e) => notificar(e?.response?.data?.mensagem || 'Não foi possível salvar o afastamento'))
      .finally(() => setSalvando(''));
  };

  if (af) {
    return (
      <div className="small mt-2 p-2 rounded" onClick={(e) => e.stopPropagation()}
        style={{ background: af.revisar ? '#fdeaec' : '#f1e8ff',
          border: `1px solid ${af.revisar ? '#e8a1aa' : '#c4b0f0'}` }}>
        <div>
          <i className={`fas ${af.revisar ? 'fa-bell' : 'fa-user-injured'} me-1`}
            style={{ color: af.revisar ? '#a11d2b' : '#5b21b6' }}></i>
          <b>{af.rotulo}</b>
          {af.obs && <span className="text-muted"> · {af.obs}</span>}
        </div>
        <div className="text-muted" style={{ fontSize: '.72rem' }}>
          marcado em {af.marcado_em}{af.marcado_por ? ` por ${af.marcado_por}` : ''}
          {af.revisado_em ? ` · última confirmação ${af.revisado_em}` : ''}
          {' · '}
          {af.revisar
            ? <b style={{ color: '#a11d2b' }}>lembrete vencido em {af.lembrar_em}: continua afastado(a)?</b>
            : <>próximo lembrete {af.lembrar_em}</>}
        </div>
        <div className="d-flex gap-1 mt-1 flex-wrap">
          <button className="btn btn-sm btn-outline-secondary py-0 px-2" disabled={!!salvando}
            onClick={() => enviar('renovar')} title="Confirma que continua afastado(a) e lembra de novo em 30 dias">
            <i className={`fas ${salvando === 'renovar' ? 'fa-spinner fa-spin' : 'fa-rotate'} me-1`}></i>
            Continua afastado(a) (+30 dias)
          </button>
          <button className="btn btn-sm btn-outline-success py-0 px-2" disabled={!!salvando}
            onClick={() => enviar('remover')} title="Encerra o afastamento: volta aos alertas de férias">
            <i className={`fas ${salvando === 'remover' ? 'fa-spinner fa-spin' : 'fa-person-walking-arrow-right'} me-1`}></i>
            Voltou ao trabalho
          </button>
        </div>
      </div>
    );
  }

  if (!abrindo) {
    return (
      <button type="button" className="btn btn-link btn-sm p-0 mt-1 d-block"
        style={{ fontSize: '.78rem', textDecoration: 'none', color: '#5b21b6' }}
        onClick={(e) => { e.stopPropagation(); setAbrindo(true); }}>
        <i className="fas fa-user-injured me-1"></i>Marcar afastamento / licença
      </button>
    );
  }

  return (
    <div className="mt-2 p-2 rounded" onClick={(e) => e.stopPropagation()}
      style={{ background: '#f8f5ff', border: '1px solid #c4b0f0' }}>
      <select className="form-select form-select-sm" value={motivo} autoFocus
        onChange={(e) => setMotivo(e.target.value)}>
        <option value="">Tipo de afastamento / licença…</option>
        {Object.entries(MOTIVOS_AFASTAMENTO).map(([k, r]) => <option key={k} value={k}>{r}</option>)}
      </select>
      <input className="form-control form-control-sm mt-1" maxLength={1000}
        placeholder="Observação (opcional): desde quando, previsão de retorno — sem CID, diagnóstico ou atestado"
        value={obs} onChange={(e) => setObs(e.target.value)} />
      <div className="d-flex gap-1 mt-1 align-items-center">
        <button className="btn btn-sm btn-success py-0 px-2" disabled={!motivo || !!salvando}
          onClick={() => enviar('marcar')}>
          <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-check'} me-1`}></i>Marcar
        </button>
        <button className="btn btn-sm btn-light border py-0 px-2" disabled={!!salvando}
          onClick={() => setAbrindo(false)}>Cancelar</button>
        <span className="small text-muted ms-1">sai dos alertas · lembrete a cada 30 dias</span>
      </div>
    </div>
  );
};

const MarcaAfastamento = ({ item }) => {
  const af = item?.afastamento;
  if (!af) return null;
  return (
    <span className="badge ms-2"
      style={{ background: af.revisar ? '#fdeaec' : '#f1e8ff', color: af.revisar ? '#a11d2b' : '#5b21b6',
        border: `1px solid ${af.revisar ? '#e8a1aa' : '#c4b0f0'}` }}
      title={`${af.rotulo}${af.obs ? ` — ${af.obs}` : ''} · lembrete ${af.lembrar_em}`}>
      <i className="fas fa-user-injured me-1"></i>{af.revisar ? 'afastamento a revisar' : 'afastado(a)'}
    </span>
  );
};

const MarcaObs = ({ item }) => {
  const obs = item?.obs || (item?.solicitacoes || []).map(s => s.obs).find(Boolean);
  if (!obs?.texto) return null;
  return (
    <i className="fas fa-comment-dots ms-2"
      style={{ color: '#8a6d00', fontSize: '.8rem' }}
      title={`${obs.texto}${obs.autor ? ` — ${obs.autor}` : ''}${obs.em ? ` (${obs.em})` : ''}`} />
  );
};

const BadgeResponsavel = ({ status }) => {
  const r = status === 'supervisorApproval'
    ? { rotulo: 'Gestor do cliente', icone: 'fa-user-tie',
        bg: '#fff5da', cor: '#8a6d00', borda: '#f1be48' }
    : status === 'accounting'
    ? { rotulo: 'Contabilidade', icone: 'fa-calculator',
        bg: '#f1e8ff', cor: '#5b21b6', borda: '#8b5cf6' }
    : { rotulo: 'Operador (nós)', icone: 'fa-user-clock',
        bg: '#e3f6fb', cor: '#0e6e86', borda: '#00b5cc' };
  return (
    <span className="badge"
      style={{ backgroundColor: r.bg, color: r.cor, border: `1.5px solid ${r.borda}` }}>
      <i className={`fas ${r.icone} me-1`}></i>{r.rotulo}
    </span>
  );
};

const SITUACAO = {
  em_andamento: { rotulo: 'Em andamento', classe: 'bg-success', icone: 'fa-plane' },
  proxima: { rotulo: 'Próxima', classe: 'bg-warning text-dark', icone: 'fa-clock' },
  encerrada: { rotulo: 'Encerrada', classe: 'bg-secondary', icone: 'fa-check' },
  cancelada: { rotulo: 'Cancelada', classe: 'bg-danger', icone: 'fa-ban' },
};

// Abas da tela do cliente. 'A vencer' lista períodos aquisitivos; as outras
// listam solicitações — daí a tabela mudar de colunas conforme a aba.
const VISOES = {
  todos: { rotulo: 'Todos', icone: 'fa-list-ul',
    nota: 'Todas as solicitações da janela selecionada, da mais recente para a mais antiga.' },
  vencer: { rotulo: 'A vencer', icone: 'fa-triangle-exclamation',
    nota: 'Quem precisa solicitar férias no próximo mês — mesma régua do A VENCER da Convenia (a data-limite para solicitar). Inclui quem nunca pediu férias.' },
  pendentes: { rotulo: 'Pendentes', icone: 'fa-user-clock',
    nota: 'Já marcadas, mas travadas na aprovação. As que já começaram sem aprovação vêm primeiro.' },
  aprovadas: { rotulo: 'Aprovadas', icone: 'fa-circle-check',
    nota: 'Aprovadas em aberto: em andamento ou por começar. As já encerradas ficam na aba Todos.' },
  em_ferias: { rotulo: 'Em férias', icone: 'fa-plane',
    nota: 'Quem está de férias hoje.' },
  canceladas: { rotulo: 'Canceladas', icone: 'fa-ban',
    nota: 'Solicitações canceladas na Convenia — ficam aqui para conferência.' },
};
const VISAO_PADRAO = 'todos';

const BpoRhFerias = () => {
  const [empresas, setEmpresas] = useState([]);
  // painéis independentes da janela de período (estado atual da operação)
  const [painel, setPainel] = useState({
    pendentes_operador: [], pendentes_gestor: [], a_vencer: [],
    dias_vencer: 180, dias_alerta: 30, pendencias_desde: '2026-01-01',
  });
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  // Reimporta as férias daquela empresa direto da Convenia. O espelho envelhece
  // entre as rodadas do sync geral e o painel passa a acusar atraso de quem já
  // tirou férias (caso Gabriela/Mecanizou). É uma chamada por colaborador,
  // então demora — por isso é botão, e não automático.
  const [sincronizando, setSincronizando] = useState(false);
  // Lista de empresas abre com o histórico completo; ao abrir uma empresa o foco
  // é a operação corrente — mesmo comportamento das Admissões/Rescisões
  const DIAS_EMPRESAS = 36500;
  const DIAS_EMPRESA_ABERTA = 30;
  const [dias, setDias] = useState(DIAS_EMPRESAS);

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  // A empresa aberta vive na URL (?cliente=) — o voltar do navegador fecha a
  // empresa em vez de pular para outra rota
  const [searchParams, setSearchParams] = useSearchParams();
  const abrirEmpresa = (emp) => setSearchParams({ cliente: String(emp.cliente_id) });
  const fecharEmpresa = () => setSearchParams({});
  const [colaboradorDetalhe, setColaboradorDetalhe] = useState(null);
  // ordem da lista de solicitações do modal: 'recente' (padrão) ou 'antiga'
  const [ordemSolic, setOrdemSolic] = useState('recente');
  const [detalheClienteId, setDetalheClienteId] = useState(null);

  // troca a anotação no modal sem recarregar tudo; a listagem atualiza depois
  const trocarObs = (escopo, chave, obs) => {
    setColaboradorDetalhe((c) => {
      if (!c) return c;
      if (escopo === 'colaborador') return { ...c, obs };
      return {
        ...c,
        solicitacoes: (c.solicitacoes || []).map(s => (
          String(s.solicitacao_id) === String(chave) ? { ...s, obs } : s)),
      };
    });
    carregar();
  };
  const [busca, setBusca] = useState('');
  const [filtroOperador, setFiltroOperador] = useState('');   // visão de empresas
  const [filtroSituacao, setFiltroSituacao] = useState('');   // visão de colaboradores
  const [filtroCargo, setFiltroCargo] = useState('');
  // Período pelo início das férias (AAAA-MM-DD dos inputs type=date)
  const [filtroDe, setFiltroDe] = useState('');
  const [filtroAte, setFiltroAte] = useState('');
  const periodoAtivo = Boolean(filtroDe || filtroAte);

  // O intervalo filtra o que o backend já devolveu — usar as datas força a
  // janela para 'Todo o período' para o filtro alcançar tudo
  useEffect(() => {
    if (periodoAtivo && dias !== DIAS_EMPRESAS) setDias(DIAS_EMPRESAS);
  }, [periodoAtivo, dias]); // eslint-disable-line react-hooks/exhaustive-deps

  // dd/mm/aaaa -> número ordenável (sem data vai para o fim)
  const dataOrdenavel = (d) => {
    if (!d) return 0;
    const p = String(d).split('/');
    return p.length === 3 ? parseInt(p[2] + p[1] + p[0], 10) : 0;
  };
  const isoOrdenavel = (d) => (d ? parseInt(d.replaceAll('-', ''), 10) : 0);

  const carregar = useCallback(() => {
    setCarregando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/ferias/`, {
        params: { dias },
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        setEmpresas(res.data.empresas || []);
        setPainel({
          pendentes_operador: res.data.pendentes_operador || [],
          pendentes_gestor: res.data.pendentes_gestor || [],
          a_vencer: res.data.a_vencer || [],
          dias_vencer: res.data.dias_vencer ?? 180,
          dias_alerta: res.data.dias_alerta ?? 30,
          pendencias_desde: res.data.pendencias_desde || '2026-01-01',
        });
        setCarregando(false);
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.erro ||
          error.response?.data?.mensagem ||
          error.message ||
          'Erro ao carregar as férias';
        setMensagem(`${errorMsg}`);
        setCarregando(false);
      });
  }, [dias]);

  useEffect(() => { carregar(); }, [carregar]);

  // O estado segue a URL: abre/fecha pela query ?cliente= (inclusive via voltar
  // do navegador) e se mantém atualizado quando a lista recarrega
  useEffect(() => {
    const id = searchParams.get('cliente');
    if (!id) {
      if (empresaSelecionada) {
        setEmpresaSelecionada(null); setBusca('');
        setDias(DIAS_EMPRESAS);          // volta à visão de carteira completa
      }
      return;
    }
    const emp = empresas.find(e => String(e.cliente_id) === id);
    if (emp && emp !== empresaSelecionada) {
      setEmpresaSelecionada(emp);
      // foco na operação corrente, salvo quando vem da lista de próximas
      if (!periodoAtivo) {
        setDias(searchParams.get('aberto') ? DIAS_EMPRESAS : DIAS_EMPRESA_ABERTA);
      }
    }
  }, [searchParams, empresas]); // eslint-disable-line react-hooks/exhaustive-deps

  // No alerta de férias o item já traz o operador do cliente, então o
  // filtro de operador da página vale para ele também
  const porOperador = useCallback(
    (lista) => (filtroOperador ? lista.filter(x => x.operador === filtroOperador) : lista),
    [filtroOperador]);

  // ─── alerta de férias da página principal ───
  // Solicitações paradas em aprovação (gestor ou nossa fila) cujas férias
  // começam em até 30 dias — ou já começaram. É o que precisa andar HOJE.
  // Aprovações não autorizadas com as férias chegando: nossa fila (RH e
  // assinatura) e a do gestor do cliente, na mesma lista e sujeitas à mesma
  // janela de proximidade. Contabilidade fica fora — não é executada por nós.
  const urgentesAprovacao = React.useMemo(() =>
    porOperador([...painel.pendentes_gestor, ...painel.pendentes_operador]
      .filter(p => p.status !== 'accounting')
      .filter(p => p.dias_para_inicio != null && p.dias_para_inicio <= painel.dias_alerta))
      .sort((a, b) => (a.dias_para_inicio ?? 9e9) - (b.dias_para_inicio ?? 9e9)),
    [painel, porOperador]);
  const urgentesJaComecaram =
    urgentesAprovacao.filter(p => p.dias_para_inicio < 0).length;

  // Segunda frente do alerta: quem entrou na janela do 'Solicitar até' e ainda
  // NÃO TEM solicitação nenhuma — antes de aprovar, alguém precisa pedir.
  const urgentesSemSolicitacao = React.useMemo(() =>
    porOperador(painel.a_vencer.filter(v =>
      // a régua é o 'Solicitar até': ainda dá para pedir, mas está no limite
      !v.atrasado && !v.afastado
      && v.prazo_dias != null && v.prazo_dias <= painel.dias_alerta
      && !v.solicitacao_status))
      .sort((a, b) => (a.prazo_dias ?? 9e9) - (b.prazo_dias ?? 9e9)),
    [painel, porOperador]);

  // Terceira frente: quem JÁ PASSOU do fim do concessivo e continua sem
  // solicitação. Ficava invisível na tela — a lista acima corta em !vencido —,
  // e era justamente esse grupo que o modal de atraso cobrava. Só quem tem
  // direito a férias (CLT/Jovem Aprendiz) entra.
  const urgentesVencidos = React.useMemo(() =>
    porOperador(painel.a_vencer.filter(v =>
      v.atrasado && v.ferias_devidas && !v.solicitacao_status && !v.afastado
      // período aquisitivo anterior à admissão é erro de cadastro na Convenia,
      // não atraso desta empresa: fica fora do alerta
      && !v.periodo_anterior_admissao))
      // agrupa por colaborador: quem tem vários aquisitivos vencidos vem
      // repetido, e a anotação é uma só
      .reduce((acc, v) => {
        const k = `${v.cliente_id}-${v.convenia_id}`;
        const atual = acc.get(k);
        if (!atual) acc.set(k, { ...v, periodos: 1 });
        else {
          atual.periodos += 1;
          if ((v.prazo_dias ?? 0) < (atual.prazo_dias ?? 0)) {
            acc.set(k, { ...v, periodos: atual.periodos });
          }
        }
        return acc;
      }, new Map()),
    [painel, porOperador]);
  // afastados / de licença (em dia ou com lembrete vencido), um por pessoa
  const listaAfastados = React.useMemo(() => {
    const m = new Map();
    for (const v of porOperador(painel.a_vencer.filter(x => x.afastamento))) {
      const k = `${v.cliente_id}-${v.convenia_id}`;
      const atual = m.get(k);
      if (!atual) m.set(k, { ...v, periodos: 1 });
      else atual.periodos += 1;
    }
    return [...m.values()].sort((a, z) =>
      (a.afastamento.dias_para_lembrar ?? 0) - (z.afastamento.dias_para_lembrar ?? 0));
  }, [painel, porOperador]);
  const listaVencidos = React.useMemo(
    () => [...urgentesVencidos.values()]
      .sort((a, b) => (a.prazo_dias ?? 0) - (b.prazo_dias ?? 0)),
    [urgentesVencidos]);

  // Vencimentos da empresa aberta. Vem da consulta global, que cobre TODOS os
  // ativos — inclusive quem não tem solicitação e por isso não aparece na
  // tabela de colaboradores abaixo.
  // A régua do 'A vencer' é a data-limite para SOLICITAR ('Solicitar até'),
  // não o fim do concessivo: é o prazo que a operação tem de cumprir.
  const aVencerEmpresa = React.useMemo(() => {
    if (!empresaSelecionada) return [];
    return painel.a_vencer
      .filter(v => v.cliente_id === empresaSelecionada.cliente_id)
      .filter(v => !v.atrasado
        && v.prazo_dias != null
        && v.prazo_dias <= painel.dias_alerta)
      .sort((a, b) => (a.prazo_dias ?? 9e9) - (b.prazo_dias ?? 9e9));
  }, [painel, empresaSelecionada]);

  // Passou do 'Solicitar até': não é mais 'a vencer', é atraso — aparece num
  // aviso próprio e exige o motivo do operador
  const vencidosEmpresaLista = React.useMemo(() => {
    if (!empresaSelecionada) return [];
    return painel.a_vencer
      .filter(v => v.cliente_id === empresaSelecionada.cliente_id && v.atrasado)
      .sort((a, b) => (a.prazo_dias ?? 0) - (b.prazo_dias ?? 0));
  }, [painel, empresaSelecionada]);

  // Solicitações da empresa que ainda não foram aprovadas — de quem depende
  // (gestor do cliente ou nossa fila) e quão perto está a data de início
  const semAprovacaoEmpresa = React.useMemo(() => {
    if (!empresaSelecionada) return [];
    const id = empresaSelecionada.cliente_id;
    return [
      ...painel.pendentes_gestor.filter(p => p.cliente_id === id),
      ...painel.pendentes_operador.filter(p => p.cliente_id === id),
    ].sort((a, b) => (a.dias_para_inicio ?? 9e9) - (b.dias_para_inicio ?? 9e9));
  }, [painel, empresaSelecionada]);
  const jaComecaramSemAprovacao =
    semAprovacaoEmpresa.filter(p => p.dias_para_inicio != null && p.dias_para_inicio < 0).length;

  // Abre o modal com o DETALHAMENTO COMPLETO do colaborador (endpoint próprio:
  // todas as solicitações sem janela + todos os períodos com prazos). O dado da
  // listagem entra na hora como prévia e é substituído quando o detalhe chega —
  // essencial no 'A vencer', onde o colaborador pode nem ter solicitação.
  const abrirDetalheColaborador = (clienteId, conveniaId, previa) => {
    // o endpoint de detalhe devolve o nome da empresa, não o id — e as
    // anotações são gravadas por cliente_id, então guardamos aqui
    setDetalheClienteId(clienteId);
    if (previa) setColaboradorDetalhe(previa);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/ferias/colaborador/`, {
        params: { cliente_id: clienteId, convenia_id: conveniaId },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => setColaboradorDetalhe(res.data.colaborador))
      .catch(() => {
        if (!previa) setMensagem('Não foi possível carregar o detalhe do colaborador');
      });
  };

  // No alerta da página principal o clique só abre o modal — não navega para a
  // empresa. O endpoint de detalhe recebe cliente_id + convenia_id, então não
  // precisa da empresa selecionada, e fechar o modal devolve o alerta intacto.
  const abrirDoAlerta = (item) => {
    const emp = empresas.find(e => e.cliente_id === item.cliente_id);
    const colab = (emp?.colaboradores || []).find(c => c.convenia_id === item.convenia_id);
    abrirDetalheColaborador(item.cliente_id, item.convenia_id, colab || null);
  };

  // A API pública da Convenia não expõe aprovação de férias — o caminho real é
  // o painel deles. O botão abre o módulo de férias e copia o nome para colar
  // na busca (cada cliente é uma conta Convenia; o login é do operador).
  const CONVENIA_FERIAS_URL = 'https://app.convenia.com.br/ferias';
  const abrirNaConvenia = (item) => {
    const nome = nomeCompleto(item);
    try { navigator.clipboard?.writeText(nome); } catch { /* sem clipboard, só abre */ }
    window.open(CONVENIA_FERIAS_URL, '_blank', 'noopener');
    setMensagem(`Convenia aberta — "${nome}" copiado, é só colar na busca`);
  };

  // ─── aviso de férias a vencer por e-mail ───
  const [avisoFerias, setAvisoFerias] = useState(null);   // {item, dados?, carregando}
  const [avisoDest, setAvisoDest] = useState('');
  const [avisoOperador, setAvisoOperador] = useState('');
  const [enviandoAviso, setEnviandoAviso] = useState(false);

  const abrirAviso = (item) => {
    setAvisoFerias({ item, carregando: true });
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/ferias/aviso/previa/`, {
        params: { cliente_id: item.cliente_id, convenia_id: item.convenia_id,
                  concessivo_fim: item.concessivo_fim },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setAvisoFerias({ item, dados: res.data, carregando: false });
        setAvisoDest(res.data.destinatarios || '');
        setAvisoOperador(res.data.operador_sugerido_id || '');
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.erro || 'Não foi possível montar a prévia'}`);
        setAvisoFerias(null);
      });
  };

  const enviarAviso = () => {
    const { item, dados } = avisoFerias;
    if (!avisoOperador) { setMensagem('Escolha o operador que assina o envio'); return; }
    if (!avisoDest.trim()) { setMensagem('Informe ao menos um destinatário'); return; }
    setEnviandoAviso(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bporh/ferias/aviso/enviar/`, {
        cliente_id: item.cliente_id,
        convenia_id: item.convenia_id,
        concessivo_fim: item.concessivo_fim,
        operador_id: avisoOperador,
        destinatarios: avisoDest,
        assunto: dados.assunto,
        corpo_html: dados.corpo_html,
      }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        // marca como avisado na tela sem recarregar a lista inteira
        const agora = new Date().toLocaleString('pt-BR',
          { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        setPainel((pn) => ({
          ...pn,
          a_vencer: pn.a_vencer.map(v =>
            v.cliente_id === item.cliente_id && v.convenia_id === item.convenia_id
              && v.concessivo_fim === item.concessivo_fim
              ? { ...v, aviso_enviado_em: agora.replace(',', '') } : v),
        }));
        setAvisoFerias(null);
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || error.response?.data?.erro || 'Falha no envio'}`);
      })
      .finally(() => setEnviandoAviso(false));
  };

  // visDestino: em qual aba da tela do cliente aterrissar quando o clique vem
  // de fora dela (dentro do cliente, a aba atual é mantida)
  const abrirDoPainel = (item, visDestino) => {
    const emp = empresas.find(e => e.cliente_id === item.cliente_id);
    const colab = (emp?.colaboradores || []).find(c => c.convenia_id === item.convenia_id);
    abrirDetalheColaborador(item.cliente_id, item.convenia_id, colab || null);
    const p = { cliente: String(item.cliente_id), aberto: '1' };
    const visAtual = searchParams.get('vis');
    const vis = (visAtual && VISOES[visAtual]) ? visAtual : visDestino;
    if (vis && vis !== VISAO_PADRAO) p.vis = vis;
    setSearchParams(p);
  };

  const empresasFiltradas = React.useMemo(() =>
    filtroOperador ? empresas.filter(e => e.operador === filtroOperador) : empresas,
    [empresas, filtroOperador]);

  const operadoresDisponiveis = React.useMemo(() =>
    [...new Set(empresas.map(e => e.operador).filter(Boolean))].sort(),
    [empresas]);

  const totalColaboradores = empresasFiltradas.reduce((s, e) => s + (e.total || 0), 0);

  const nomeCompleto = (c) =>
    [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.nome_social || '(sem cadastro no espelho)';

  const resumo = React.useMemo(() => {
    const contagem = { em_andamento: 0, proxima: 0, encerrada: 0, cancelada: 0 };
    let solicitacoes = 0;
    empresasFiltradas.forEach(e => {
      solicitacoes += e.total_solicitacoes || 0;
      Object.keys(contagem).forEach(k => { contagem[k] += e[k] || 0; });
    });
    return { ...contagem, solicitacoes };
  }, [empresasFiltradas]);

  const opcoesCargo = React.useMemo(() =>
    [...new Set((empresaSelecionada?.colaboradores || []).map(c => c.cargo).filter(Boolean))].sort(),
    [empresaSelecionada]);

  useEffect(() => {  // troca de empresa limpa os filtros de colaborador
    setFiltroCargo(''); setFiltroSituacao(''); setBusca('');
    setFiltroDe(''); setFiltroAte('');
  }, [empresaSelecionada?.cliente_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Uma linha por SOLICITAÇÃO, não por colaborador: quem tem duas solicitações
  // no mesmo intervalo (uma cancelada e outra ativa, por exemplo) precisa ver as
  // duas — colapsar por pessoa escondia a cancelada atrás de um '+N'
  const solicitacoesFiltradas = React.useMemo(() => {
    if (!empresaSelecionada) return [];
    const b = busca.trim().toLowerCase();
    const de = isoOrdenavel(filtroDe);
    const ate = isoOrdenavel(filtroAte);
    return empresaSelecionada.colaboradores
      .flatMap(c => (c.solicitacoes || []).map(s => ({ c, s })))
      .filter(({ c, s }) => {
        if (filtroCargo && c.cargo !== filtroCargo) return false;
        if (filtroSituacao && s.situacao !== filtroSituacao) return false;
        if (de || ate) {
          // sem data de início fica fora quando há filtro de período
          const d = dataOrdenavel(s.inicio);
          if (!d) return false;
          if (de && d < de) return false;
          if (ate && d > ate) return false;
        }
        if (b && ![c.nome, c.sobrenome, c.cargo, c.departamento, c.email]
          .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
        return true;
      })
      .sort((x, y) => dataOrdenavel(y.s.inicio) - dataOrdenavel(x.s.inicio));
  }, [empresaSelecionada, busca, filtroCargo, filtroSituacao, filtroDe, filtroAte]);

  const colaboradoresNaLista = React.useMemo(
    () => new Set(solicitacoesFiltradas.map(({ c }) => c.convenia_id)).size,
    [solicitacoesFiltradas]);

  // ─── abas da tela do cliente ───
  const visao = VISOES[searchParams.get('vis')] ? searchParams.get('vis') : VISAO_PADRAO;
  const trocarVisao = (nova) => {
    const p = { cliente: String(empresaSelecionada.cliente_id) };
    if (searchParams.get('aberto')) p.aberto = '1';
    if (nova !== VISAO_PADRAO) p.vis = nova;
    setSearchParams(p);
  };

  // O que cada aba lista. 'vencer' e 'pendentes' são estado atual (não dependem
  // da janela de período); as demais saem das solicitações da janela.
  const listaVisao = React.useMemo(() => ({
    todos: solicitacoesFiltradas,
    // só as aprovadas em aberto (em andamento ou por começar) — as encerradas
    // são histórico e ficam na aba Todos
    aprovadas: solicitacoesFiltradas.filter(({ s }) =>
      s.status === 'approved' && (s.situacao === 'em_andamento' || s.situacao === 'proxima')),
    em_ferias: solicitacoesFiltradas.filter(({ s }) => s.situacao === 'em_andamento'),
    canceladas: solicitacoesFiltradas.filter(({ s }) => s.situacao === 'cancelada'),
  }), [solicitacoesFiltradas]);

  const contagemVisao = {
    todos: solicitacoesFiltradas.length,
    vencer: aVencerEmpresa.length,
    pendentes: semAprovacaoEmpresa.length,
    aprovadas: listaVisao.aprovadas.length,
    em_ferias: listaVisao.em_ferias.length,
    canceladas: listaVisao.canceladas.length,
  };

  const baixarCsv = (nome, cabecalho, registros) => {
    const esc = (v) => `"${String(v ?? '').replaceAll('"', '""')}"`;
    const csv = [cabecalho.map(esc).join(';'),
                 ...registros.map(r => r.map(esc).join(';'))].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = nome;
    a.click();
    URL.revokeObjectURL(a.href);
    setMensagem(`Relatório exportado (${registros.length} linha${registros.length > 1 ? 's' : ''})`);
  };

  const COLUNAS_VENCER = ['Empresa', 'Operador', 'Colaborador', 'Cargo', 'Vínculo (Convenia)',
    'Vínculo — origem',
    'Departamento', 'Aquisitivo Início', 'Aquisitivo Fim', 'Solicitar Até', 'Concessivo Até',
    'Pagar Até (no limite)',
    'Dias Para Solicitar', 'Situação', 'Dias Para o Concessivo', 'Concessivo',
    'Etapa Convenia', 'Início Solicitado', 'Saldo (dias)'];
  const linhaVencer = (v) => [
    v.cliente, v.operador || '', nomeCompleto(v), v.cargo || '',
    v.modelo_contratacao || '',
    v.modelo_origem === 'cargo' ? 'presumido pelo cargo' : 'cadastrado na Convenia',
    v.departamento || '',
    v.aquisitivo_inicio || '', v.aquisitivo_fim || '', v.solicitar_ate || '', v.concessivo_fim || '',
    v.pagamento_ate || '',
    v.prazo_dias ?? '', v.atrasado ? 'Atrasado (passou do Solicitar até)' : 'A vencer',
    v.dias_para_vencer ?? '', v.vencido ? 'Concessivo vencido' : '',
    v.solicitacao_status
      ? (ETAPA_CONVENIA[v.solicitacao_status]?.rotulo || v.solicitacao_status)
      : 'Sem solicitação',
    v.solicitacao_inicio || '', v.saldo ?? '',
  ];

  const exportarVencerEmpresa = () => {
    // vencidos vão junto, sinalizados na coluna Situação — some da tela quem
    // resolve, não quem exporta
    const tudo = [...vencidosEmpresaLista, ...aVencerEmpresa];
    if (tudo.length === 0) { setMensagem('Nada para exportar'); return; }
    const slug = (empresaSelecionada?.cliente || 'empresa')
      .normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')
      .replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase();
    baixarCsv(`ferias_a_vencer_${slug}.csv`, COLUNAS_VENCER, tudo.map(linhaVencer));
  };

  const exportarExcel = () => {
    const linhas = empresaSelecionada
      // na empresa aberta o Excel sai igual ao que está na tela, linha por linha
      ? solicitacoesFiltradas.map(({ c, s }) => ({
          empresa: empresaSelecionada.cliente, operador: empresaSelecionada.operador, c, s,
        }))
      : empresasFiltradas.flatMap(e =>
          (e.colaboradores || []).flatMap(c => (c.solicitacoes || []).map(s => ({
            empresa: e.cliente, operador: e.operador, c, s,
          }))));
    if (linhas.length === 0) {
      setMensagem('Nada para exportar com os filtros atuais');
      return;
    }
    const esc = (v) => `"${String(v ?? '').replaceAll('"', '""')}"`;
    const csv = [
      ['Empresa', 'Operador', 'Colaborador', 'Cargo', 'Vínculo (Convenia)', 'Vínculo — origem',
       'Departamento',
       'Início', 'Fim', 'Pagar até', 'Dias Solicitados', 'Situação', 'Etapa Convenia',
       'Abono Pecuniário (dias)', 'Antecipação 13º', 'Saldo (dias)', 'Justificativa',
       'E-mail'].map(esc).join(';'),
      ...linhas.map(({ empresa, operador, c, s }) => [
        empresa, operador || '', nomeCompleto(c), c.cargo || '',
        c.modelo_contratacao || '',
        c.modelo_origem === 'cargo' ? 'presumido pelo cargo' : 'cadastrado na Convenia',
        c.departamento || '',
        s.inicio || '', s.fim || '', s.pagamento_ate || '', s.dias_solicitados ?? '',
        SITUACAO[s.situacao]?.rotulo || s.situacao || '',
        ETAPA_CONVENIA[s.status]?.rotulo || s.status || '',
        s.abono_dias ?? '', s.decimo_terceiro ? 'Sim' : 'Não',
        c.saldo_total ?? '', s.justificativa || '', c.email || '',
      ].map(esc).join(';')),
    ].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const periodo = periodoAtivo ? `${filtroDe || 'inicio'}_a_${filtroAte || 'hoje'}` : 'todo_periodo';
    a.download = `relatorio_ferias_${periodo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    setMensagem(`Relatório exportado (${linhas.length} solicitaç${linhas.length > 1 ? 'ões' : 'ão'})`);
  };

  // Prazo do 'Solicitar até' (data-limite para as férias serem pedidas):
  // vermelho se já passou E dentro da janela de alerta (30 dias) — nesse ponto
  // as férias precisam ser programadas agora; azul quando ainda há folga
  const BadgePrazo = ({ dias, ate }) => {
    if (dias == null) return <span className="text-muted">-</span>;
    if (dias < 0) {
      return (
        <span className="badge bg-danger"
          title={`Passou da data-limite para solicitar${ate ? ` (${ate})` : ''} — programar imediatamente`}>
          <i className="fas fa-triangle-exclamation me-1"></i>
          atrasado {Math.abs(dias)}d
        </span>
      );
    }
    const critico = dias <= painel.dias_alerta;
    return (
      <span className={`badge ${critico ? 'bg-danger' : 'bg-info text-dark'}`}
        title={critico
          ? `Faltam ${dias} dias para o limite de solicitação${ate ? ` (${ate})` : ''} — programar agora`
          : `Faltam ${dias} dias para o limite de solicitação${ate ? ` (${ate})` : ''}`}>
        <i className={`fas ${critico ? 'fa-triangle-exclamation' : 'fa-clock'} me-1`}></i>
        {dias}d
      </span>
    );
  };

  // Usada no painel da carteira e na página do cliente (sem a coluna Empresa)
  const TabelaAVencer = ({ lista, comEmpresa = true }) => (
    <div className="table-responsive">
      <table className="table table-striped table-hover align-middle">
        <thead>
          <tr>
            {comEmpresa && <th><i className="fas fa-building me-2"></i>Empresa</th>}
            <th><i className="fas fa-user me-2"></i>Colaborador</th>
            <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
            <th><i className="fas fa-file-signature me-2"></i>Modelo de contratação</th>
            <th><i className="fas fa-hourglass-start me-2"></i>Aquisitivo</th>
            <th><i className="fas fa-calendar-check me-2"></i>Solicitar até</th>
            <th><i className="fas fa-calendar-xmark me-2"></i>Concessivo até</th>
            <th><i className="fas fa-money-bill-wave me-2"></i>Pagar até</th>
            <th><i className="fas fa-clock me-2"></i>Prazo</th>
            <th><i className="fas fa-columns me-2"></i>Etapa Convenia</th>
            <th><i className="fas fa-sun me-2"></i>Saldo</th>
            {comEmpresa && <th><i className="fas fa-user-tie me-2"></i>Operador</th>}
            <th className="text-center">Detalhes</th>
          </tr>
        </thead>
        <tbody>
          {lista.map((v, i) => (
            <tr key={`${v.cliente_id}-${v.convenia_id}-${v.concessivo_fim}-${i}`}
              style={{ cursor: 'pointer' }}
              onClick={() => abrirDoPainel(v)}>
              {comEmpresa && (
                <td className="fw-semibold" style={{ color: '#00205b' }}>{v.cliente}</td>
              )}
              <td className="fw-bold">{nomeCompleto(v)}<MarcaObs item={v} /><MarcaAfastamento item={v} /></td>
              <td>{v.cargo || '-'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <BadgeModelo modelo={v.modelo_contratacao} origem={v.modelo_origem} />
              </td>
              <td className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {v.aquisitivo_inicio} → {v.aquisitivo_fim}
              </td>
              <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {v.solicitar_ate || '-'}
                {v.vencido && (
                  <span className="badge bg-danger ms-2"
                    title={`Concessivo encerrado em ${v.concessivo_fim} — exposição a pagamento em dobro (art. 137)`}>
                    concessivo vencido
                  </span>
                )}
              </td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>{v.concessivo_fim || '-'}</td>
              <td><BadgePagamento s={v} /></td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <BadgePrazo dias={v.prazo_dias} ate={v.prazo_ate} />
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {v.solicitacao_status ? (
                  <>
                    <BadgeEtapa status={v.solicitacao_status} />
                    {v.solicitacao_inicio && (
                      <small className="text-muted ms-1"
                        title="Início das férias solicitadas"
                        style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {v.solicitacao_inicio}
                      </small>
                    )}
                  </>
                ) : (
                  <span className="badge bg-light text-muted border"
                    title="Nenhuma solicitação em aberto na Convenia — as férias ainda precisam ser pedidas">
                    Sem solicitação
                  </span>
                )}
              </td>
              <td>
                <span className="badge bg-primary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {v.saldo} dias
                </span>
              </td>
              {comEmpresa && <td className="text-muted">{v.operador || '-'}</td>}
              <td className="text-center">
                <button className="btn btn-sm btn-outline-primary"
                  onClick={(e) => { e.stopPropagation(); abrirDoPainel(v); }}
                  title="Abrir na empresa">
                  <i className="fas fa-eye"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const BadgeSituacao = ({ situacao }) => {
    const s = SITUACAO[situacao];
    if (!s) return <span className="badge bg-light text-muted border">{situacao || '-'}</span>;
    return (
      <span className={`badge ${s.classe}`}>
        <i className={`fas ${s.icone} me-1`}></i>{s.rotulo}
      </span>
    );
  };

  return (
    <div className="bporh-ferias-page">
      {/* a trava "Ação obrigatória: férias vencidas sem justificativa" saiu em
          29/09/2026: a cobrança vai no boletim do agente ao gestor */}
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-umbrella-beach header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Férias
              {empresaSelecionada && <span className="text-muted"> · {empresaSelecionada.cliente}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Link to="/BpoRhAtividadesAdmissao?tipo=Férias" className="btn btn-outline-primary"
              title="Cadastrar o template de atividades de férias por cliente">
              <i className="fas fa-list-check me-2"></i>
              Atividades
            </Link>
            {empresaSelecionada && (
              <button className="btn btn-outline-secondary" onClick={fecharEmpresa}>
                <i className="fas fa-arrow-left me-2"></i>
                Empresas
              </button>
            )}
            <select
              className="form-select"
              style={{ width: 'auto' }}
              value={dias}
              onChange={(e) => setDias(parseInt(e.target.value))}
              disabled={periodoAtivo}
              title={periodoAtivo
                ? 'Desativado enquanto houver filtro por data de início'
                : 'Janela: férias com término dentro do período (inclui futuras)'}
            >
              <option value={30}>Térm. últimos 30 dias + futuras</option>
              <option value={90}>Térm. últimos 90 dias + futuras</option>
              <option value={180}>Térm. últimos 180 dias + futuras</option>
              <option value={365}>Últimos 12 meses + futuras</option>
              <option value={36500}>Todo o período</option>
            </select>

            {empresaSelecionada && (
              <div className="d-flex align-items-center gap-1 border rounded px-2 py-1"
                title="Filtra pela data de início das férias">
                <i className="fas fa-plane-departure text-muted" style={{ fontSize: '.82rem' }}></i>
                <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
                  value={filtroDe} max={filtroAte || undefined}
                  onChange={(e) => setFiltroDe(e.target.value)} aria-label="Início de" />
                <span className="text-muted small">até</span>
                <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
                  value={filtroAte} min={filtroDe || undefined}
                  onChange={(e) => setFiltroAte(e.target.value)} aria-label="Início até" />
                {periodoAtivo && (
                  <button className="btn btn-sm btn-light p-1" title="Limpar período"
                    onClick={() => { setFiltroDe(''); setFiltroAte(''); }}>
                    <i className="fas fa-times text-danger"></i>
                  </button>
                )}
              </div>
            )}

            {empresaSelecionada && (
              <button className="btn btn-primary" disabled={sincronizando || carregando}
                title={`Reimporta da Convenia os períodos e solicitações de férias da ${empresaSelecionada.cliente}. Demora alguns minutos.`}
                onClick={() => {
                  setSincronizando(true);
                  const accessToken = localStorage.getItem('access');
                  axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/ferias/sincronizar/`,
                    { cliente_id: empresaSelecionada.cliente_id },
                    { headers: { Authorization: `Bearer ${accessToken}` } })
                    .then((res) => { setMensagem(`${res.data.mensagem}`); carregar(); })
                    .catch((err) => setMensagem(
                      `${err.response?.data?.mensagem || 'Não foi possível sincronizar'}`))
                    .finally(() => setSincronizando(false));
                }}>
                <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-down'} me-2`}></i>
                {sincronizando ? 'Sincronizando...' : 'Buscar da Convenia'}
              </button>
            )}
            <button className="btn btn-outline-success" onClick={exportarExcel} disabled={carregando}
              title="Exporta o relatório de férias do período filtrado (abre no Excel)">
              <i className="fas fa-file-excel me-2"></i>
              Excel
            </button>
            <button className="btn btn-outline-primary" onClick={carregar} disabled={carregando} title="Atualizar">
              <i className={`fas fa-sync-alt ${carregando ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 mb-0">Carregando férias...</p>
          </div>
        ) : !empresaSelecionada ? (
          <>
            {/* ─── CARTEIRA DE EMPRESAS (sempre visível) ─── */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="badge fs-6"
                style={{ backgroundColor: '#00205b', fontVariantNumeric: 'tabular-nums' }}>
                <i className="fas fa-umbrella-beach me-1"></i>
                Total: {totalColaboradores} colaborador{totalColaboradores === 1 ? '' : 'es'} com férias no período
              </span>
              <span className="badge bg-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {empresasFiltradas.length} empresas
              </span>
              <select
                className="form-select form-select-sm ms-auto"
                style={{ maxWidth: '220px' }}
                value={filtroOperador}
                onChange={(e) => setFiltroOperador(e.target.value)}
                title="Filtrar por operador"
              >
                <option value="">Todos os operadores</option>
                {operadoresDisponiveis.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* ─── RESUMO DA CARTEIRA ─── */}
            {empresasFiltradas.length > 0 && (
              <div className="adm-resumo">
                <div className="adm-kpi">
                  <span className="v">{resumo.solicitacoes}</span>
                  <span className="k">Solicitações</span>
                </div>
                <div className="adm-kpi">
                  <span className="v">{empresasFiltradas.length}</span>
                  <span className="k">Empresas</span>
                </div>
                <div className="adm-resumo-sep" />
                <div className="adm-kpi ok">
                  <span className="v">{resumo.em_andamento}</span>
                  <span className="k">Em andamento</span>
                </div>
                <div className="adm-kpi pend">
                  <span className="v">{resumo.proxima}</span>
                  <span className="k">A iniciar</span>
                </div>
                <div className="adm-resumo-sep" />
                <div className="adm-resumo-pend">
                  <div className="titulo">
                    {resumo.proxima > 0 ? 'Próximas férias por empresa' : 'Situação da carteira'}
                  </div>
                  <div className="adm-chips">
                    {resumo.proxima > 0 ? (
                      empresasFiltradas
                        .filter(e => e.proxima > 0)
                        .sort((a, b) => b.proxima - a.proxima)
                        .slice(0, 6)
                        .map(e => (
                          <span key={e.cliente_id} className="adm-chip">
                            <i className="fas fa-clock" style={{ fontSize: '.7rem' }}></i>
                            {e.cliente} <b>{e.proxima}</b>
                          </span>
                        ))
                    ) : (
                      <span className="adm-chip tudo-ok">
                        <i className="fas fa-circle-check" style={{ fontSize: '.72rem' }}></i>
                        Nenhuma férias a iniciar
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {empresasFiltradas.length === 0 ? (
              <div className="alert alert-info d-flex flex-wrap align-items-center gap-2">
                <span>
                  <i className="fas fa-info-circle me-2"></i>
                  {filtroOperador
                    ? <>Nenhuma férias para <b>{filtroOperador}</b> nesta janela.</>
                    : <>Nenhuma férias no período selecionado. As solicitações chegam pela
                        sincronização e pelos webhooks da Convenia.</>}
                </span>
                {filtroOperador && (
                  <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => setFiltroOperador('')}>
                    <i className="fas fa-users me-2"></i>Ver todos os operadores
                  </button>
                )}
                {dias !== DIAS_EMPRESAS && (
                  <button className="btn btn-sm btn-outline-primary"
                    onClick={() => setDias(DIAS_EMPRESAS)}>
                    <i className="fas fa-calendar-days me-2"></i>Ver todo o período
                  </button>
                )}
              </div>
            ) : (
              <div className="adm-grid">
                {empresasFiltradas.map((emp, i) => {
                  // o que a operação precisa preparar são as férias a iniciar
                  const aIniciar = emp.proxima || 0;
                  const emCurso = emp.em_andamento || 0;
                  const pct = emp.total > 0
                    ? Math.round(((emp.total - aIniciar) / emp.total) * 100) : 0;
                  return (
                    <div
                      key={emp.cliente_id}
                      className={`adm-card ferias ${aIniciar > 0 ? 'tem-pendencia' : 'tudo-ok'}`}
                      style={{ '--i': i }}
                      onClick={() => abrirEmpresa(emp)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') abrirEmpresa(emp); }}
                    >
                      <div className="adm-card-top">
                        <h3 className="adm-empresa">{emp.cliente}</h3>
                        <span className="adm-total" title="Colaboradores com férias no período">
                          {emp.total}
                        </span>
                      </div>

                      <div className="adm-operador">
                        <i className="fas fa-user-tie"></i>
                        {emp.operador || 'Sem operador'}
                      </div>

                      <div className="adm-progresso" aria-hidden="true">
                        <span style={{ width: `${pct}%` }} />
                      </div>

                      <div className="adm-legenda">
                        {aIniciar > 0 ? (
                          <div>
                            <span className="pend">
                              <i className="fas fa-clock me-1"></i>
                              {aIniciar} a iniciar
                            </span>
                            {emCurso > 0 && (
                              <>
                                <span className="text-muted mx-1">·</span>
                                <span className="text-muted">{emCurso} em andamento</span>
                              </>
                            )}
                          </div>
                        ) : emCurso > 0 ? (
                          <span className="ok">
                            <i className="fas fa-plane me-1"></i>
                            {emCurso} em andamento
                          </span>
                        ) : (
                          <span className="ok">
                            <i className="fas fa-circle-check me-1"></i>
                            Nada a iniciar
                          </span>
                        )}
                      </div>

                      <div className="adm-card-acao">
                        Ver colaboradores <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ─── ALERTA DE FÉRIAS: o que precisa andar já ─── */}
            {(urgentesAprovacao.length > 0 || urgentesSemSolicitacao.length > 0
              || listaVencidos.length > 0 || listaAfastados.length > 0) && (
              <div className="ferias-urgente mb-4">
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <h5 className="fw-bold mb-0" style={{ color: '#a11d2b' }}>
                    <i className="fas fa-bell me-2"></i>
                    Alerta de férias
                  </h5>
                  {urgentesJaComecaram > 0 && (
                    <span className="badge bg-danger" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {urgentesJaComecaram} férias já começaram sem aprovação
                    </span>
                  )}
                  <span className="badge bg-warning text-dark" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {urgentesAprovacao.length + urgentesSemSolicitacao.length} pendência{urgentesAprovacao.length + urgentesSemSolicitacao.length === 1 ? '' : 's'}
                  </span>
                </div>
                <p className="text-muted small mb-2">
                  O que precisa andar antes do prazo: aprovações paradas com as férias
                  chegando e colaboradores no limite legal que ainda nem solicitaram.
                  A etapa de Contabilidade fica fora — não é executada por nós.
                  <br />
                  <b>Pagar até</b>: 2 dias úteis antes do início (CLT art. 145) — sábado,
                  domingo e feriado não contam. Para quem ainda não marcou férias, a data
                  vem tracejada com <i>no limite</i> — é o pagamento na pior hipótese
                  (férias começando no último dia do concessivo); marcando antes, o
                  pagamento antecipa junto.
                </p>

                {urgentesAprovacao.length > 0 && (
                <>
                <h6 className="fw-bold mt-3 mb-1" style={{ color: '#212529' }}>
                  <i className="fas fa-user-check me-2" style={{ color: '#dc3545' }}></i>
                  Aprovações não autorizadas — férias começam em até {painel.dias_alerta} dias
                  <span className="badge bg-danger ms-2"
                    style={{ fontVariantNumeric: 'tabular-nums' }}>{urgentesAprovacao.length}</span>
                </h6>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Colaborador</th>
                        <th>Empresa</th>
                        <th>Modelo</th>
                        <th>Início</th>
                        <th>Pagar até</th>
                        <th>Quando começa</th>
                        <th>Quem tem que aprovar</th>
                        <th>Etapa na Convenia</th>
                        <th>Operador</th>
                        <th className="text-end">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {urgentesAprovacao.map((p) => {
                        const comecou = p.dias_para_inicio < 0;
                        return (
                          <tr key={p.solicitacao_id} style={{ cursor: 'pointer' }}
                            onClick={() => abrirDoAlerta(p)}>
                            <td className="fw-bold">{nomeCompleto(p)}<MarcaObs item={p} /></td>
                            <td className="fw-semibold" style={{ color: '#00205b' }}>{p.cliente}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <BadgeModelo modelo={p.modelo_contratacao} origem={p.modelo_origem} />
                            </td>
                            <td style={{ fontVariantNumeric: 'tabular-nums' }}>{p.inicio || '-'}</td>
                            <td><BadgePagamento s={p} /></td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {comecou ? (
                                <span className="badge bg-danger">
                                  <i className="fas fa-triangle-exclamation me-1"></i>
                                  começou há {Math.abs(p.dias_para_inicio)}d
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark">
                                  <i className="fas fa-clock me-1"></i>
                                  em {p.dias_para_inicio}d
                                </span>
                              )}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <BadgeResponsavel status={p.status} />
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <BadgeEtapa status={p.status} />
                            </td>
                            <td className="text-muted">{p.operador || '-'}</td>
                            <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-sm btn-outline-success me-1"
                                onClick={(e) => { e.stopPropagation(); abrirNaConvenia(p); }}
                                title="Aprovar na Convenia — abre o módulo de férias e copia o nome para a busca">
                                <i className="fas fa-arrow-up-right-from-square"></i>
                              </button>
                              <button className="btn btn-sm btn-outline-primary"
                                onClick={(e) => { e.stopPropagation(); abrirDoAlerta(p); }}
                                title="Abrir o detalhamento">
                                <i className="fas fa-eye"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
                )}

                {listaVencidos.length > 0 && (
                <>
                <h6 className="fw-bold mt-3 mb-1" style={{ color: '#212529' }}>
                  <i className="fas fa-triangle-exclamation me-2" style={{ color: '#a11d2b' }}></i>
                  Já passaram do limite legal
                  <span className="badge bg-danger ms-2"
                    style={{ fontVariantNumeric: 'tabular-nums' }}>{listaVencidos.length}</span>
                  {listaVencidos.filter(v => v.motivo_pendente).length > 0 && (
                    <span className="badge ms-2"
                      style={{ background: '#fff8e6', color: '#8a6d00',
                        border: '1px solid #f1be48' }}
                      title="Sem o motivo do atraso registrado, o aviso de férias não é enviado">
                      <i className="fas fa-comment-slash me-1"></i>
                      {listaVencidos.filter(v => v.motivo_pendente).length} sem motivo do atraso
                    </span>
                  )}
                </h6>
                <p className="small text-muted mb-2">
                  Férias não concedidas dentro do período concessivo são pagas <b>em dobro</b>{' '}
                  (art. 137 da CLT).
                </p>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Colaborador</th>
                        <th>Empresa</th>
                        <th>Admissão</th>
                        <th>Venceu em</th>
                        <th>Atraso</th>
                        <th>Saldo</th>
                        <th style={{ minWidth: 230 }}>Motivo do atraso</th>
                        <th>Operador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listaVencidos.map((v) => (
                        <tr key={`venc-${v.cliente_id}-${v.convenia_id}`}
                          style={{ background: v.motivo_pendente ? '#fdeaec' : undefined }}>
                          <td className="fw-bold">
                            {nomeCompleto(v)}
                            {v.periodos > 1 && (
                              <span className="badge bg-light text-muted border ms-2">
                                {v.periodos} períodos
                              </span>
                            )}
                          </td>
                          <td>{v.cliente}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{v.admissao || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}
                            title={v.concessivo_fim ? `Concessivo até ${v.concessivo_fim}` : ''}>
                            {v.prazo_ate || v.concessivo_fim}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span className="badge bg-danger"
                              title="Dias desde a data-limite para solicitar">
                              {v.prazo_dias != null ? `${-v.prazo_dias}d` : '—'}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-primary">
                              {v.saldo != null ? `${v.saldo} dias` : '—'}
                            </span>
                          </td>
                          <td>
                            {v.obs ? (
                              <div className="small"
                                title={`${v.obs.texto}${v.obs.autor ? ` — ${v.obs.autor}` : ''}`}>
                                <i className="fas fa-comment-dots me-1 text-muted"></i>
                                {v.obs.texto.length > 60
                                  ? `${v.obs.texto.slice(0, 60)}…` : v.obs.texto}
                              </div>
                            ) : (
                              <span className="badge bg-danger">
                                <i className="fas fa-triangle-exclamation me-1"></i>
                                motivo obrigatório
                              </span>
                            )}
                            <AnotacaoFerias
                              clienteId={v.cliente_id}
                              escopo="colaborador"
                              chave={v.convenia_id}
                              obs={v.obs}
                              rotulo="Por que estas férias estouraram o limite legal?"
                              onSalvo={(o) => trocarObs('colaborador', v.convenia_id, o)}
                            />
                            <AfastamentoFerias item={v} onMudou={() => carregar()} />
                          </td>
                          <td className="text-muted">{v.operador || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
                )}

                {listaAfastados.length > 0 && (
                <>
                <h6 className="fw-bold mt-3 mb-1" style={{ color: '#212529' }}>
                  <i className="fas fa-user-injured me-2" style={{ color: '#5b21b6' }}></i>
                  Afastados / licença
                  <span className="badge ms-2" style={{ background: '#f1e8ff', color: '#5b21b6',
                    border: '1px solid #c4b0f0', fontVariantNumeric: 'tabular-nums' }}>{listaAfastados.length}</span>
                  {listaAfastados.filter(v => v.afastamento.revisar).length > 0 && (
                    <span className="badge bg-danger ms-2">
                      <i className="fas fa-bell me-1"></i>
                      {listaAfastados.filter(v => v.afastamento.revisar).length} com lembrete vencido
                    </span>
                  )}
                </h6>
                <p className="small text-muted mb-2">
                  Fora dos alertas e do boletim enquanto o afastamento estiver confirmado. A cada 30 dias
                  o lembrete vence e o caso volta pedindo confirmação.
                </p>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Colaborador</th>
                        <th>Empresa</th>
                        <th>Solicitar até</th>
                        <th>Saldo</th>
                        <th style={{ minWidth: 300 }}>Afastamento</th>
                        <th>Operador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listaAfastados.map((v) => (
                        <tr key={`afast-${v.cliente_id}-${v.convenia_id}`}
                          style={{ background: v.afastamento.revisar ? '#fdeaec' : undefined }}>
                          <td className="fw-bold">
                            {nomeCompleto(v)}
                            {v.periodos > 1 && (
                              <span className="badge bg-light text-muted border ms-2">{v.periodos} períodos</span>
                            )}
                          </td>
                          <td>{v.cliente}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{v.prazo_ate || v.concessivo_fim || '—'}</td>
                          <td>
                            <span className="badge bg-primary">{v.saldo != null ? `${v.saldo} dias` : '—'}</span>
                          </td>
                          <td><AfastamentoFerias item={v} onMudou={() => carregar()} /></td>
                          <td className="text-muted">{v.operador || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
                )}

                {urgentesSemSolicitacao.length > 0 && (
                <>
                <h6 className="fw-bold mt-3 mb-1" style={{ color: '#212529' }}>
                  <i className="fas fa-calendar-xmark me-2" style={{ color: '#dc3545' }}></i>
                  No limite legal e ainda sem solicitação
                  <span className="badge bg-danger ms-2"
                    style={{ fontVariantNumeric: 'tabular-nums' }}>{urgentesSemSolicitacao.length}</span>
                  {/* esta lista corta em !vencido, então nunca teria alguém com
                      motivo pendente: quem estourou o prazo está na seção acima */}
                </h6>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Colaborador</th>
                        <th>Empresa</th>
                        <th>Modelo</th>
                        <th>Data limite (concessivo)</th>
                        <th>Pagar até</th>
                        <th>Prazo</th>
                        <th>Saldo</th>
                        <th style={{ minWidth: 230 }}>Motivo do atraso</th>
                        <th>Operador</th>
                        <th>Aviso</th>
                        <th className="text-end">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {urgentesSemSolicitacao.map((v, i) => (
                        <tr key={`${v.cliente_id}-${v.convenia_id}-${i}`}
                          style={{ cursor: 'pointer',
                            background: v.motivo_pendente ? '#fdeaec' : undefined }}
                          onClick={() => abrirDoAlerta(v)}>
                          <td className="fw-bold">{nomeCompleto(v)}<MarcaObs item={v} /><MarcaAfastamento item={v} /></td>
                          <td className="fw-semibold" style={{ color: '#00205b' }}>{v.cliente}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <BadgeModelo modelo={v.modelo_contratacao} origem={v.modelo_origem} />
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}
                            title={`Data-limite para solicitar${v.concessivo_fim ? ` · concessivo até ${v.concessivo_fim} (dobra, art. 137)` : ''}`}>
                            {v.prazo_ate || v.concessivo_fim || '-'}
                          </td>
                          <td><BadgePagamento s={v} /></td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <BadgePrazo dias={v.prazo_dias} ate={v.prazo_ate} />
                          </td>
                          <td>
                            <span className="badge bg-primary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {v.saldo} dias
                            </span>
                          </td>
                          {/* Motivo do atraso: anotação normal para quem ainda
                              está no prazo, OBRIGATÓRIA para quem já estourou —
                              sem ela o aviso de férias é recusado no backend. */}
                          <td onClick={(e) => e.stopPropagation()}>
                            {v.obs?.texto ? (
                              <div className="small" title={`${v.obs.texto}${v.obs.autor
                                ? ` — ${v.obs.autor}` : ''}${v.obs.em ? ` (${v.obs.em})` : ''}`}>
                                <i className="fas fa-comment-dots me-1 text-muted"></i>
                                {v.obs.texto.length > 70
                                  ? `${v.obs.texto.slice(0, 70)}…` : v.obs.texto}
                              </div>
                            ) : v.motivo_obrigatorio ? (
                              <span className="badge bg-danger">
                                <i className="fas fa-triangle-exclamation me-1"></i>
                                motivo obrigatório
                              </span>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                            <AnotacaoFerias
                              clienteId={v.cliente_id}
                              escopo="colaborador"
                              chave={v.convenia_id}
                              obs={v.obs}
                              rotulo={v.motivo_obrigatorio
                                ? 'Por que estas férias estouraram o limite legal?'
                                : 'Anotação sobre este colaborador...'}
                              onSalvo={(o) => trocarObs('colaborador', v.convenia_id, o)}
                            />
                            <AfastamentoFerias item={v} onMudou={() => carregar()} />
                          </td>
                          <td className="text-muted">{v.operador || '-'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {v.aviso_enviado_em ? (
                              <span className="badge bg-success"
                                title={`Aviso enviado em ${v.aviso_enviado_em}`}>
                                <i className="fas fa-envelope-circle-check me-1"></i>
                                avisado
                              </span>
                            ) : (
                              <span className="badge bg-light text-muted border">não avisado</span>
                            )}
                          </td>
                          <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-sm btn-outline-success me-1"
                              onClick={(e) => { e.stopPropagation(); abrirNaConvenia(v); }}
                              title="Abrir o módulo de férias da Convenia (copia o nome para a busca)">
                              <i className="fas fa-arrow-up-right-from-square"></i>
                            </button>
                            <button
                              className={`btn btn-sm me-1 ${v.aviso_enviado_em
                                ? 'btn-outline-secondary' : 'btn-outline-warning'}`}
                              onClick={(e) => { e.stopPropagation(); abrirAviso(v); }}
                              title={v.aviso_enviado_em
                                ? `Já avisado em ${v.aviso_enviado_em} — reenviar`
                                : 'Enviar aviso de férias a vencer por e-mail'}>
                              <i className="fas fa-envelope"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-primary"
                              onClick={(e) => { e.stopPropagation(); abrirDoAlerta(v); }}
                              title="Abrir o detalhamento">
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
                )}
              </div>
            )}

          </>
        ) : (
          <>
            {/* ─── TELA DO CLIENTE ─── */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="badge fs-6" style={{ backgroundColor: '#00205b' }}>
                <i className="fas fa-building me-1"></i> {empresaSelecionada.cliente}
              </span>
              <span className="badge bg-secondary">
                <i className="fas fa-user-tie me-1"></i> {empresaSelecionada.operador || 'Sem operador'}
              </span>
              {periodoAtivo && (
                <span className="badge bg-light text-dark border">
                  <i className="fas fa-filter me-1"></i>
                  início {filtroDe ? filtroDe.split('-').reverse().join('/') : 'início'}
                  {' até '}
                  {filtroAte ? filtroAte.split('-').reverse().join('/') : 'hoje'}
                </span>
              )}
            </div>

            {/* ─── MENU DE VISÕES ─── */}
            <div className="ferias-pills">
              {Object.entries(VISOES).map(([chave, meta]) => {
                const n = contagemVisao[chave];
                // crítico = algo visível na aba exige ação imediata
                const critico = (chave === 'vencer'
                    && aVencerEmpresa.some(v => v.dias_para_solicitar != null && v.dias_para_solicitar < 0))
                  || (chave === 'pendentes' && jaComecaramSemAprovacao > 0);
                return (
                  <button
                    key={chave}
                    type="button"
                    className={`ferias-pill ${visao === chave ? 'ativa' : ''} ${critico ? 'critica' : ''}`}
                    onClick={() => trocarVisao(chave)}
                    title={meta.nota}
                  >
                    <i className={`fas ${meta.icone}`}></i>
                    {meta.rotulo}
                    <span className="ferias-pill-num">({n})</span>
                  </button>
                );
              })}
            </div>

            <div className="ferias-bloco-nota mb-3">
              <i className="fas fa-circle-info me-2"></i>
              {VISOES[visao].nota}
              {(visao === 'vencer' || visao === 'pendentes') && (
                <> Independe do filtro de período — é o estado de hoje.</>
              )}
              {visao === 'vencer' && (
                <> Entra quem tem o <b>Solicitar até</b> a {painel.dias_alerta} dias ou
                  menos (inclusive já estourado). O concessivo é o limite legal: passado
                  ele, as férias são pagas em dobro (art. 137 da CLT).</>
              )}
              {visao === 'pendentes' && (
                <> A coluna <b>Quem tem que aprovar</b> diz de quem a solicitação
                  depende: <b>Gestor</b> é o cliente, <b>Operador</b> é a nossa fila
                  (RH ou assinatura); <b>Contabilidade</b> é etapa de terceiro.
                  Solicitações iniciadas antes
                  de <b>{painel.pendencias_desde.slice(0, 4)}</b> ficam de fora —
                  registros antigos nunca concluídos na Convenia.</>
              )}
            </div>

            {/* filtros: valem para as abas que listam solicitações */}
            {visao !== 'vencer' && visao !== 'pendentes' && (
              <div className="row g-2 mb-3">
                <div className="col-md-5">
                  <select className="form-select form-select-sm" value={filtroCargo}
                    onChange={(e) => setFiltroCargo(e.target.value)}>
                    <option value="">Todos os cargos</option>
                    {opcoesCargo.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="col-md-7">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Nome, cargo, departamento, e-mail..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* ─── A VENCER: períodos aquisitivos ─── */}
            {visao === 'vencer' && (
              <>
                {aVencerEmpresa.length === 0 ? (
                  <div className="alert alert-success">
                    <i className="fas fa-circle-check me-2"></i>
                    Ninguém com prazo de solicitação a menos de {painel.dias_alerta} dias
                    nesta empresa.
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-end mb-2">
                      <button className="btn btn-sm btn-outline-success"
                        onClick={exportarVencerEmpresa} title="Exporta esta lista">
                        <i className="fas fa-file-excel me-2"></i>Excel
                      </button>
                    </div>
                    <TabelaAVencer lista={aVencerEmpresa} comEmpresa={false} />
                  </>
                )}
              </>
            )}

            {/* ─── PENDENTES: quem tem que aprovar ─── */}
            {visao === 'pendentes' && (
              semAprovacaoEmpresa.length === 0 ? (
                <div className="alert alert-success">
                  <i className="fas fa-circle-check me-2"></i>
                  Nenhuma solicitação parada na aprovação nesta empresa.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover align-middle">
                    <thead>
                      <tr>
                        <th><i className="fas fa-user me-2"></i>Colaborador</th>
                        <th><i className="fas fa-file-signature me-2"></i>Modelo</th>
                        <th><i className="fas fa-plane-departure me-2"></i>Início</th>
                        <th><i className="fas fa-plane-arrival me-2"></i>Fim</th>
                        <th><i className="fas fa-sun me-2"></i>Dias</th>
                        <th><i className="fas fa-money-bill-wave me-2"></i>Pagar até</th>
                        <th><i className="fas fa-hourglass-half me-2"></i>Quando começa</th>
                        <th><i className="fas fa-user-check me-2"></i>Quem tem que aprovar</th>
                        <th><i className="fas fa-columns me-2"></i>Etapa na Convenia</th>
                        <th className="text-center">Detalhes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {semAprovacaoEmpresa.map((p) => {
                        const comecou = p.dias_para_inicio != null && p.dias_para_inicio < 0;
                        return (
                          <tr key={p.solicitacao_id}
                            style={{
                              cursor: 'pointer',
                              backgroundColor: comecou ? '#fdeaec' : undefined,
                            }}
                            onClick={() => abrirDoPainel(p)}>
                            <td className="fw-bold">{nomeCompleto(p)}<MarcaObs item={p} /></td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <BadgeModelo modelo={p.modelo_contratacao} origem={p.modelo_origem} />
                            </td>
                            <td style={{ fontVariantNumeric: 'tabular-nums' }}>{p.inicio || '-'}</td>
                            <td style={{ fontVariantNumeric: 'tabular-nums' }}>{p.fim || '-'}</td>
                            <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {p.dias_solicitados ?? '-'}
                            </td>
                            <td><BadgePagamento s={p} /></td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {p.dias_para_inicio == null ? (
                                <span className="text-muted">-</span>
                              ) : comecou ? (
                                <span className="badge bg-danger"
                                  title="As férias já começaram e a aprovação não saiu">
                                  <i className="fas fa-triangle-exclamation me-1"></i>
                                  começou há {Math.abs(p.dias_para_inicio)}d
                                </span>
                              ) : (
                                <span className={`badge ${p.dias_para_inicio <= painel.dias_alerta
                                  ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
                                  <i className="fas fa-clock me-1"></i>
                                  em {p.dias_para_inicio}d
                                </span>
                              )}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <BadgeResponsavel status={p.status} />
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <BadgeEtapa status={p.status} />
                            </td>
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-sm btn-outline-success me-1"
                                onClick={(e) => { e.stopPropagation(); abrirNaConvenia(p); }}
                                title="Aprovar na Convenia — abre o módulo de férias e copia o nome para a busca">
                                <i className="fas fa-arrow-up-right-from-square"></i>
                              </button>
                              <button className="btn btn-sm btn-outline-primary"
                                onClick={(e) => { e.stopPropagation(); abrirDoPainel(p); }}
                                title="Ver férias e saldos">
                                <i className="fas fa-eye"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* ─── TODOS / APROVADAS / EM FÉRIAS / CANCELADAS: solicitações ─── */}
            {listaVisao[visao] && (
              listaVisao[visao].length === 0 ? (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  Nenhuma solicitação nesta aba
                  {(filtroCargo || busca.trim() || periodoAtivo) && ' com os filtros atuais'}.
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                    <span className="badge bg-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {listaVisao[visao].length} solicitaç{listaVisao[visao].length === 1 ? 'ão' : 'ões'}
                      {visao === 'todos' && <> · {colaboradoresNaLista} colaborador{colaboradoresNaLista === 1 ? '' : 'es'}</>}
                    </span>
                    <button className="btn btn-sm btn-outline-success ms-auto"
                      onClick={exportarExcel} title="Exporta as solicitações desta empresa">
                      <i className="fas fa-file-excel me-2"></i>Excel
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover align-middle">
                      <thead>
                        <tr>
                          <th><i className="fas fa-user me-2"></i>Colaborador</th>
                          <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
                          <th><i className="fas fa-file-signature me-2"></i>Modelo</th>
                          <th><i className="fas fa-sitemap me-2"></i>Departamento</th>
                          <th><i className="fas fa-plane-departure me-2"></i>Início</th>
                          <th><i className="fas fa-plane-arrival me-2"></i>Fim</th>
                          <th><i className="fas fa-sun me-2"></i>Dias</th>
                          <th><i className="fas fa-money-bill-wave me-2"></i>Pagar até</th>
                          <th><i className="fas fa-flag me-2"></i>Situação</th>
                          <th><i className="fas fa-columns me-2"></i>Etapa Convenia</th>
                          <th><i className="fas fa-hourglass-half me-2"></i>Saldo</th>
                          <th className="text-center">Detalhes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listaVisao[visao].map(({ c, s }) => (
                          <tr key={s.solicitacao_id}
                            style={{
                              cursor: 'pointer',
                              // cancelada esmaecida: está ali para conferência,
                              // não é férias que vai acontecer
                              opacity: s.situacao === 'cancelada' ? .62 : 1,
                            }}
                            onClick={() => abrirDetalheColaborador(empresaSelecionada.cliente_id, c.convenia_id, c)}>
                            <td className="fw-bold">
                              {nomeCompleto(c)}
                              {c.desligado && (
                                <span className="badge bg-secondary ms-2"
                                  title={c.data_desligamento
                                    ? `Desligado em ${c.data_desligamento} — férias acertadas na rescisão`
                                    : 'Desligado — férias acertadas na rescisão'}>
                                  <i className="fas fa-user-minus me-1"></i>desligado
                                </span>
                              )}
                            </td>
                            <td>{c.cargo || '-'}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <BadgeModelo modelo={c.modelo_contratacao} origem={c.modelo_origem} />
                            </td>
                            <td>{c.departamento || '-'}</td>
                            <td style={{ fontVariantNumeric: 'tabular-nums' }}>{s.inicio || '-'}</td>
                            <td style={{ fontVariantNumeric: 'tabular-nums' }}>{s.fim || '-'}</td>
                            <td style={{ fontVariantNumeric: 'tabular-nums' }}>{s.dias_solicitados ?? '-'}</td>
                            <td><BadgePagamento s={s} /></td>
                            <td style={{ whiteSpace: 'nowrap' }}><BadgeSituacao situacao={s.situacao} /></td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <BadgeEtapa status={s.status} />
                            </td>
                            <td>
                              {c.saldo_total == null ? (
                                <span className="badge bg-light text-muted border">-</span>
                              ) : (
                                <span className={`badge ${c.saldo_total > 0 ? 'bg-primary' : 'bg-secondary'}`}
                                  title="Saldo do colaborador (soma dos períodos aquisitivos)">
                                  {c.saldo_total} dias
                                </span>
                              )}
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={(e) => { e.stopPropagation(); abrirDetalheColaborador(empresaSelecionada.cliente_id, c.convenia_id, c); }}
                                title="Ver férias e saldos"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            )}

          </>
        )}
      </div>

      {/* ─── MODAL: AVISO DE FÉRIAS A VENCER (prévia + confirmação) ─── */}
      {avisoFerias && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-envelope me-2"></i>
                    Aviso de férias a vencer
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {avisoFerias.item && `${nomeCompleto(avisoFerias.item)} · ${avisoFerias.item.cliente}`}
                    {avisoFerias.item?.aviso_enviado_em && ` · já avisado em ${avisoFerias.item.aviso_enviado_em}`}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setAvisoFerias(null)} disabled={enviandoAviso}></button>
              </div>

              <div className="modal-body">
                {avisoFerias.carregando ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="text-muted mt-2 mb-0">Montando a prévia...</p>
                  </div>
                ) : (
                  <>
                    <div className="row g-2 mb-3">
                      <div className="col-md-7">
                        <label className="form-label small fw-bold mb-1 text-muted">
                          Destinatários (edite à vontade — separe por ; )
                        </label>
                        <input type="text" className="form-control form-control-sm"
                          value={avisoDest}
                          onChange={(e) => setAvisoDest(e.target.value)}
                          placeholder="email@exemplo.com; outro@exemplo.com" />
                        {!avisoFerias.dados.email_gestor && avisoFerias.dados.supervisor && (
                          <small className="text-muted">
                            Gestor "{avisoFerias.dados.supervisor}" sem e-mail no espelho —
                            acrescente manualmente se quiser copiá-lo.
                          </small>
                        )}
                      </div>
                      <div className="col-md-5">
                        <label className="form-label small fw-bold mb-1 text-muted">
                          Enviar com a conta de
                        </label>
                        <select className="form-select form-select-sm" value={avisoOperador}
                          onChange={(e) => setAvisoOperador(e.target.value)}>
                          <option value="">Selecione o operador...</option>
                          {(avisoFerias.dados.operadores || []).map(o => (
                            <option key={o.id} value={o.id} disabled={!o.pronto}>
                              {o.nome}{!o.pronto ? ' (sem senha de app)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="small text-muted mb-1">
                      <b>Assunto:</b> {avisoFerias.dados.assunto}
                    </div>
                    <div className="border rounded" style={{ height: '420px', overflow: 'hidden' }}>
                      <iframe title="Prévia do aviso de férias"
                        srcDoc={avisoFerias.dados.corpo_previa}
                        style={{ width: '100%', height: '100%', border: 0 }} />
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary"
                  onClick={() => setAvisoFerias(null)} disabled={enviandoAviso}>
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </button>
                {!avisoFerias.carregando && (
                  <button type="button" className="btn btn-success"
                    onClick={enviarAviso} disabled={enviandoAviso}>
                    <i className={`fas ${enviandoAviso ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                    {enviandoAviso ? 'Enviando...'
                      : avisoFerias.item?.aviso_enviado_em ? 'Confirmar reenvio' : 'Confirmar envio'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: FÉRIAS DO COLABORADOR ─── */}
      {colaboradorDetalhe && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-umbrella-beach me-2"></i>
                    {nomeCompleto(colaboradorDetalhe)}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {colaboradorDetalhe.cliente || empresaSelecionada?.cliente}
                    {colaboradorDetalhe.cargo && ` · ${colaboradorDetalhe.cargo}`}
                    {colaboradorDetalhe.data_admissao && ` · admissão ${colaboradorDetalhe.data_admissao}`}
                    {colaboradorDetalhe.desligado
                      ? ` · desligado${colaboradorDetalhe.data_desligamento ? ` em ${colaboradorDetalhe.data_desligamento}` : ''}`
                      : colaboradorDetalhe.status_colaborador && ` · ${colaboradorDetalhe.status_colaborador}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setColaboradorDetalhe(null)}></button>
              </div>

              <div className="modal-body">
                {/* Anotação geral do colaborador: vale mesmo sem férias marcadas,
                    que é o caso de quem está na aba 'A vencer' */}
                <AnotacaoFerias
                  clienteId={detalheClienteId}
                  escopo="colaborador"
                  chave={colaboradorDetalhe.convenia_id}
                  obs={colaboradorDetalhe.obs}
                  rotulo="Anotar sobre as férias deste colaborador"
                  onSalvo={(obs) => trocarObs('colaborador', colaboradorDetalhe.convenia_id, obs)}
                />
                <AfastamentoFerias
                  item={painel.a_vencer.find(v => v.cliente_id === detalheClienteId
                    && String(v.convenia_id) === String(colaboradorDetalhe.convenia_id))
                    || { cliente_id: detalheClienteId, convenia_id: colaboradorDetalhe.convenia_id }}
                  onMudou={() => carregar()} />

                {/* Solicitações — todas, quando o detalhe completo já chegou */}
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3 mt-3 d-flex align-items-center flex-wrap gap-2">
                  <span>
                    <i className="fas fa-plane-departure me-2"></i>
                    Solicitações de férias ({(colaboradorDetalhe.solicitacoes || []).length})
                  </span>
                  {(colaboradorDetalhe.solicitacoes || []).length > 1 && (
                    <button type="button" className="btn btn-sm btn-light border ms-auto fw-normal"
                      style={{ fontSize: '.78rem', padding: '2px 10px' }}
                      onClick={() => setOrdemSolic(o => (o === 'recente' ? 'antiga' : 'recente'))}
                      title="Alterna a ordem da lista">
                      <i className={`fas ${ordemSolic === 'recente' ? 'fa-arrow-down-wide-short' : 'fa-arrow-up-wide-short'} me-2`}></i>
                      {ordemSolic === 'recente' ? 'Mais recente primeiro' : 'Mais antiga primeiro'}
                    </button>
                  )}
                </h6>
                {(colaboradorDetalhe.solicitacoes || []).length === 0 && (
                  <div className="alert alert-light border small">
                    <i className="fas fa-circle-info me-2 text-primary"></i>
                    Este colaborador nunca solicitou férias na Convenia — por isso só
                    aparece na aba <b>A vencer</b>.
                  </div>
                )}
                {[...(colaboradorDetalhe.solicitacoes || [])]
                  .sort((a, b) => (ordemSolic === 'recente'
                    ? dataOrdenavel(b.inicio) - dataOrdenavel(a.inicio)
                    : dataOrdenavel(a.inicio) - dataOrdenavel(b.inicio)))
                  .map((s) => (
                  <div key={s.solicitacao_id} className="border rounded p-3 mb-2 bg-light">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                      <div className="fw-bold">
                        <i className="fas fa-calendar me-2 text-primary"></i>
                        {s.inicio} → {s.fim}
                        <span className="text-muted fw-normal ms-2">({s.dias_solicitados} dias)</span>
                      </div>
                      <div className="d-flex gap-1 flex-wrap">
                        <BadgePagamento s={s} />
                        <BadgeSituacao situacao={s.situacao} />
                        <BadgeEtapa status={s.status} />
                      </div>
                    </div>
                    <div className="small text-muted d-flex gap-3 flex-wrap">
                      {/* abono é quantidade de dias vendidos, não sim/não */}
                      <span>
                        {s.abono_dias > 0 ? (
                          <>
                            <i className="fas fa-check text-success me-1"></i>
                            Abono pecuniário
                            <b className="ms-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {s.abono_dias} dia{s.abono_dias === 1 ? '' : 's'}
                            </b>
                          </>
                        ) : s.abono_dias === 0 ? (
                          <>
                            <i className="fas fa-times text-danger me-1"></i>
                            Sem abono pecuniário
                          </>
                        ) : (
                          <>
                            <i className="fas fa-question text-muted me-1"></i>
                            Abono pecuniário não informado
                          </>
                        )}
                      </span>
                      <span>
                        <i className={`fas ${s.decimo_terceiro ? 'fa-check text-success' : 'fa-times text-danger'} me-1`}></i>
                        Antecipação 13º
                      </span>
                    </div>
                    {s.justificativa && (
                      <div className="small mt-2">
                        <i className="fas fa-comment me-1 text-muted"></i>
                        {s.justificativa}
                      </div>
                    )}
                    <AnotacaoFerias
                      clienteId={detalheClienteId}
                      escopo="solicitacao"
                      chave={s.solicitacao_id}
                      obs={s.obs}
                      rotulo="Anotar sobre estas férias"
                      onSalvo={(obs) => trocarObs('solicitacao', s.solicitacao_id, obs)}
                    />
                  </div>
                ))}

                {/* Períodos aquisitivos / saldo */}
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3 mt-3">
                  <i className="fas fa-hourglass-half me-2"></i>
                  Períodos aquisitivos e saldo ({(colaboradorDetalhe.periodos || []).length})
                  {colaboradorDetalhe.saldo_total != null && (
                    <span className="badge bg-primary ms-2">
                      {colaboradorDetalhe.saldo_total} dias no total
                    </span>
                  )}
                </h6>
                {colaboradorDetalhe.vencimento?.alerta && (
                  <div className={`alert ${colaboradorDetalhe.vencimento.vencido ? 'alert-danger' : 'alert-warning'} py-2 small`}>
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    {colaboradorDetalhe.vencimento.vencido ? (
                      <>Concessivo <b>encerrado em {colaboradorDetalhe.vencimento.concessivo_fim}</b> com{' '}
                        {colaboradorDetalhe.vencimento.saldo} dias de saldo — férias fora do
                        concessivo são pagas em dobro.</>
                    ) : (
                      colaboradorDetalhe.vencimento.solicitar_ate ? (
                        <>As férias precisam começar até <b>{colaboradorDetalhe.vencimento.solicitar_ate}</b>{' '}
                          (data imposta pela Convenia{colaboradorDetalhe.vencimento.dias_para_solicitar != null
                            ? (colaboradorDetalhe.vencimento.dias_para_solicitar < 0
                              ? ` — passou há ${Math.abs(colaboradorDetalhe.vencimento.dias_para_solicitar)} dias`
                              : ` — faltam ${colaboradorDetalhe.vencimento.dias_para_solicitar} dias`) : ''}),
                          com {colaboradorDetalhe.vencimento.saldo} dias de saldo a programar. O concessivo
                          termina em {colaboradorDetalhe.vencimento.concessivo_fim}: as férias têm de acabar antes
                          dele, senão são pagas em dobro.</>
                      ) : (
                        <>Faltam <b>{colaboradorDetalhe.vencimento.dias_para_vencer} dias</b> para o
                          fim do concessivo ({colaboradorDetalhe.vencimento.concessivo_fim}), com{' '}
                          {colaboradorDetalhe.vencimento.saldo} dias de saldo a programar.</>
                      )
                    )}
                  </div>
                )}
                {(colaboradorDetalhe.periodos || []).length === 0 ? (
                  <div className="text-muted small">Sem períodos aquisitivos no espelho.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-striped align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Aquisitivo</th>
                          <th>Solicitar até</th>
                          <th>Concessivo até</th>
                          <th>Prazo</th>
                          <th className="text-end">Saldo (dias)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {colaboradorDetalhe.periodos.map((p, i) => (
                          <tr key={i}>
                            <td>{p.aquisitivo_inicio} → {p.aquisitivo_fim}</td>
                            <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {p.dias_para_solicitar == null ? (
                                <span className="text-muted" title="Período sem saldo">-</span>
                              ) : (
                                <>
                                  {p.solicitar_ate}
                                  {p.dias_para_solicitar < 0 && (
                                    <span className="badge bg-danger ms-2"
                                      title="A data-limite para solicitar já passou">
                                      há {Math.abs(p.dias_para_solicitar)}d
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                            <td>{p.concessivo_fim || '-'}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {p.dias_para_vencer == null
                                ? <span className="text-muted" title="Período sem saldo">-</span>
                                : <BadgePrazo dias={p.dias_para_vencer} />}
                            </td>
                            <td className="text-end">
                              <span className={`badge ${p.saldo > 0 ? 'bg-primary' : 'bg-secondary'}`}>
                                {p.saldo ?? '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Contato */}
                {(colaboradorDetalhe.email || colaboradorDetalhe.celular) && (
                  <div className="small text-muted mt-3">
                    <i className="fas fa-address-book me-2"></i>
                    {[colaboradorDetalhe.email, colaboradorDetalhe.celular].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setColaboradorDetalhe(null)}>
                  <i className="fas fa-times me-2"></i>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhFerias;
